import React, { ChangeEvent, useEffect, useReducer, useState } from 'react';
import greaseRelubricateReducer, {
	initialGreaseRelubricationState,
} from './reducer';
import { greaseFillOption, text } from '../../data';
import RelubricateByPartNumber from './relubricate-by-part-number/RelubricateByPartNumber';
import {
	BearingCalculationData,
	GreaseFrequencyPayload,
} from '../../../components/bearing-search/types/types';
import {
	getGreaseFrequency,
	greaseLubricationContent,
	thickenerTypes,
} from '../../../components/bearing-search/api/api';
import CalculationResults from '../../components/CalculationResults';
import RelubricateBySpecification from './relubricate-by-specification/RelubricateBySpecification';
import SelectDropdown from '../../../components/bearing-search/components/forms/SelectDropdown';
import { SelectOption } from '../../types/types';
import { toggleImportantNote, toggleNextTools, toggleReportReady } from '../../utils/engineering-tools';
import { trackResults, trackStart, updateActionParams } from '../../utils/analytics';
import { BearingPartState } from '../../../components/bearing-search/reducers/reducer';

export type GreaseFrequencyPartPayload = Omit<
	GreaseFrequencyPayload,
	'isDBRetrieval' | 'isReportRequired' | 'thickerTypeCode'
>;

const GreaseRelubrication: React.FC = () => {
	const formDescription = greaseLubricationContent?.greaseRelubrication
		? { __html: greaseLubricationContent.greaseRelubrication }
		: undefined;

	const footerNote = greaseLubricationContent?.greaseRelubricationFooter
		? { __html: greaseLubricationContent.greaseRelubricationFooter }
		: undefined;

	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ results, setResults ] = useState<Array<BearingCalculationData | null>>(
		[],
	);
	const [ errorMessage, setErrorMessage ] = useState<string>( '' );
	const [ warningMessage, setWarningMessage ] = useState<string>( '' );
	const [ bearing, setBearing ] = useState(null);
	const [ formState, dispatch ] = useReducer(
		greaseRelubricateReducer,
		initialGreaseRelubricationState,
	);

	useEffect( () => {
		trackStart( 'greaseRelubrication' );
	}, [] );

	// display/hide save bearing dropdown
	useEffect(() => {
		// clear saved selected bearing. This resets the Save Bearing dropdown
		const event = new CustomEvent('bearingSelectedEvent', {
			detail: { hideDropdown: formState.option != 'PartNumber' },
		});
		setTimeout(() => {
			document.dispatchEvent(event);
		}, 100); // add timeout since the component fires the event before the dropdown listener is set
	}, [formState.option])

	// Show / hide recommended tools
	useEffect( () => {
		const isShown = isCalculated && ! isResultsLoading;
		toggleNextTools(isShown);
		toggleReportReady(
			isShown,
			bearing?.marketPlaceDescription,
			bearing?.primaryImage,
		);

		return () => {
			toggleNextTools( false );
		};
	}, [ isCalculated, isResultsLoading ] );

	const handleSubmit = (
		requestData: GreaseFrequencyPartPayload,
		bearing?: BearingPartState,
	) => {
		setIsCalculated( true );
		setIsResultsLoading( true );
		setErrorMessage( '' );

		setBearing(bearing);

		const request: GreaseFrequencyPayload = {
			...requestData,
			thickerTypeCode: formState.thickerTypeCode,
			isDBRetrieval: formState.option === 'PartNumber',
		};

		getGreaseFrequency( request )
			.then( ( response ) => {
				setWarningMessage(null);
				updateActionParams( 'greaseRelubrication', bearing );

				let reportData: Array<BearingCalculationData | null> = [];

				if ( response.report && response.report.length > 0 ) {
					reportData = response.report[ 0 ].data;
				}

				setResults( reportData );
				trackResults( 'greaseRelubrication', request, reportData.length, bearing?.marketPlaceDescription );

				if ( response.header.errorCount > 0 ) {
					if ( response.header.messages?.length ) {
						setErrorMessage( response.header.messages[ 0 ].messageText );
						toggleImportantNote( false );
					} else {
						toggleImportantNote( true );
					}
				} else if ( response.header.messages?.length ) {
					const warningMessage = prepareWarningMessages( response.header.messages );
					setWarningMessage(warningMessage);
					toggleImportantNote( true );
				} else {
					toggleImportantNote( true );
				}

				document.querySelector('.t-engineering-tools__results').scrollIntoView({
					behavior: 'smooth',
				});
			} )
			.finally( () => setIsResultsLoading( false ) );
	};

	const clearSearch = () => {
		setIsCalculated( false );
		setErrorMessage( '' );
	};

	const prepareWarningMessages = ( messages ) => {
		let warningMessage = '';
		for (let i = 0; i < messages.length; i++) {
			if ( warningMessage.length == 0 ) {
				warningMessage += 'Warning: ';
			}
			warningMessage += messages[ i ].messageText;
			warningMessage += ' <br /> ';
		}
		return warningMessage;
	};

	return (
		<div>
			{!! formDescription && (
				<p
					className="t-engineering-tools__description"
					dangerouslySetInnerHTML={formDescription}
				></p>
			)}

			<p>
				<span className="c-form__asterisk">*</span>
				{text.requiredMessage}
			</p>

			<div className="row form-row">
				<div className="col-md-3">
					<SelectDropdown
						id="grease_relubricate_thickener_type"
						name="thickerTypeCode"
						required={true}
						label={text.thickenerType}
						value={formState.thickerTypeCode}
						options={thickenerTypes.map( ( item ) => {
							const { code, description } = item;
							return {
								id: `grease_relubricate_thickener_type_${ code }`,
								value: code,
								label: description,
							} as SelectOption<string>;
						} )}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_thickener_type',
								payload: event.target.value,
							} )
						}
					/>
				</div>
			</div>

			<div className="t-engineering-tools__options">
				{greaseFillOption.map( ( option ) => {
					const id = `${ option.id }_relubricate`;

					return (
						<div className="m-radio" key={id}>
							<div className="m-radio__field">
								<input
									id={id}
									className="m-radio__input"
									checked={formState.option === option.value}
									type="radio"
									value={option.value}
									onChange={() =>
										dispatch( { type: 'change_option', payload: option.value } )
									}
								/>
								<label className="m-radio__label" htmlFor={id}>
									{option.label}
								</label>
							</div>
						</div>
					);
				} )}
			</div>

			{formState.option === 'PartNumber' && (
				<RelubricateByPartNumber
					typeCode={formState.thickerTypeCode}
					onSubmit={handleSubmit}
					onClear={clearSearch}
				/>
			)}

			{formState.option === 'Specification' && (
				<RelubricateBySpecification
					typeCode={formState.thickerTypeCode}
					onSubmit={handleSubmit}
					onClear={clearSearch}
				/>
			)}

			{isCalculated && (
				<CalculationResults data={results} loading={isResultsLoading} errorMessage={errorMessage} warningMessage={warningMessage}/>
			)}

			{isCalculated && ! isResultsLoading && footerNote && ! errorMessage && (
				<div
					className="t-engineering-tools__notes js-engineering-tools-notes"
					id="js-engineering-tools-notes"
					dangerouslySetInnerHTML={footerNote}
				></div>
			)}
		</div>
	);
};

export default GreaseRelubrication;
